<template>
  <div class="status-pipeline">
    <SidebarAccordion :title="'Statut ' + workflow.label" :default-open="true" v-bind="$attrs">
      <div v-if="workflow.managed" class="status-pipeline-track">
        <StatusStep v-for="step in workflow.steps" :key="step.value" :step="step" :read-only="isReadOnly" @select="selectStep" />
      </div>
      <div v-else class="status-pipeline-unmanaged">Non géré pour le moment</div>

      <div v-if="isAwaitingSignature" class="status-pipeline-validation">
        <p class="status-transition-message is-info">
          <span class="status-transition-icon"><span class="fas fa-circle-info"></span></span>
          <span>Si le devis repasse en mode création entre-temps, il faudra regénérer le PDF : la signature aura changé.</span>
        </p>
        <CustomButton @click="openSignModal" :is-compact="true" :params="signButtonParams"/>
      </div>

      <div v-if="hasPendingChange" class="status-pipeline-validation">
        <p v-if="transitionMessage" class="status-transition-message" :class="'is-' + transitionMessage.type">
          <span class="status-transition-icon">{{ isBlocked ? '⛔' : '⚠️' }}</span>
          <span>{{ transitionMessage.message }}</span>
        </p>
        <CustomButton @click="validateStatus" :is-compact="true" :params="validateButtonParams"/>
      </div>
    </SidebarAccordion>
    <ForceStatusModal ref="forceStatusModal" :callback="applyStatus" />
  </div>
</template>

<script>
import {NotificationService} from "@vues/v-common";
import {library} from '@fortawesome/fontawesome-svg-core';
import {faCircleInfo} from "@fortawesome/free-solid-svg-icons";
import {BusinessController} from "@/controllers/business/BusinessController";
import CustomButton from "@/components/forms/customs/customButton/CustomButton.vue";
import ForceStatusModal from "@/components/modals/ForceStatusModal.vue";
import {CollectionController} from "@/controllers/business/CollectionController";
import {PAYMENT_TYPE} from "@/models/paymentType";
import {CREDIT_NOTE_STATUS_ENUM} from "@/models/creditNoteStatusEnum";
import {DEPOSIT_STATUS_ENUM} from "@/models/depositStatusEnum";
import {BUSINESS_STATUS_ENUM} from "@/models/businessStatusEnum";
import {getCollectionProposal} from "@/utils";
import StatusStep from "@/components/businessEdition/status/StatusStep.vue";
import SidebarAccordion from "@/components/businessEdition/SidebarAccordion.vue";
import {isQuoteAwaitingSignature, possibleBusinessStatusSelect} from "@/models/businessStatusEnum";
import {possibleCreditNoteStatusSelect} from "@/models/creditNoteStatusEnum";
import {getDepositStatus} from "@/models/depositStatusEnum";
import {
  getStatusDocumentKind,
  getStatusWorkflow,
  isBusinessReadOnly
} from "@/components/businessEdition/status/statusWorkflow";
import {getStatusTransitionMessage, STATUS_DOCUMENT_KIND} from "@/components/businessEdition/status/statusTransition";

library.add(faCircleInfo);

export default {
  name: 'StatusSection',
  components: {CustomButton, ForceStatusModal, StatusStep, SidebarAccordion},
  // Les attributs non déclarés (open, toggle-callback...) sont transmis à l'accordéon pour pouvoir piloter son état depuis le parent
  inheritAttrs: false,
  props: {
    business: {type: Object, default: undefined},
    creditNote: {type: Object, default: undefined},
    creditNoteInputs: {type: Object, default: undefined},
    inputs: {type: Object, required: true},
    documentType: {type: String, required: true},
    callback: {type: Function, default: undefined},
    // Mode consultation : le statut est affiché mais ne peut pas être modifié
    readOnly: {type: Boolean, default: false},
    signCallback: {type: Function, default: undefined},
  },
  data() {
    return {
      signButtonParams: {text: 'Valider la signature'},
      selfInputs: this.inputs,
      selfCreditNoteInputs: this.creditNoteInputs,
      // Sélection en cours sur l'acompte (enregistrée à la validation)
      selfDepositStatus: undefined,
      // Statut forcé : encaissements du contrat, pour calculer l'encaissement associé à proposer
      collections: [],
      collectionsLoadedFor: undefined
    };
  },
  computed: {
    kind() {
      return getStatusDocumentKind(this.documentType);
    },
    isCreditNote() {
      return this.kind === STATUS_DOCUMENT_KIND.CREDIT_NOTE;
    },
    isDeposit() {
      return this.kind === STATUS_DOCUMENT_KIND.DEPOSIT;
    },
    // Statut visé qui correspond à un règlement : payé (facture, acompte) ou remboursé (avoir)
    isPaymentTarget() {
      const pending = this.workflow.pendingStatus;
      if (!pending) return false;
      if (this.isCreditNote) return pending === CREDIT_NOTE_STATUS_ENUM.REFUNDED.value;
      if (this.isDeposit) return pending === DEPOSIT_STATUS_ENUM.PAID.value;
      return this.kind === STATUS_DOCUMENT_KIND.INVOICE && pending === BUSINESS_STATUS_ENUM.PAID.value;
    },
    collectionProposal() {
      if (!this.isPaymentTarget || !this.business || this.collectionsLoadedFor !== this.business.id) return undefined;
      return getCollectionProposal(this.business, this.collections, {
        type: this.documentType,
        creditNoteNumber: this.creditNote ? this.creditNote.number : undefined
      });
    },
    isAwaitingSignature() {
      return this.kind === STATUS_DOCUMENT_KIND.QUOTE && !!this.business && isQuoteAwaitingSignature(this.business);
    },
    selectedStatus() {
      if (this.isDeposit) return this.selfDepositStatus || getDepositStatus(this.business);
      if (!this.isCreditNote) return this.inputs.value;
      return this.selfCreditNoteInputs ? this.selfCreditNoteInputs.status.value : this.creditNote && this.creditNote.status;
    },
    workflow() {
      return getStatusWorkflow({
        kind: this.kind,
        business: this.business,
        creditNote: this.creditNote,
        selectedStatus: this.selectedStatus
      });
    },
    isReadOnly() {
      return this.readOnly || this.workflow.readOnly;
    },
    hasPendingChange() {
      return !!this.workflow.pendingStatus && !this.isReadOnly;
    },
    transitionMessage() {
      if (!this.hasPendingChange) return null;
      const context = {documentType: this.kind, business: this.business, creditNote: this.creditNote};
      return getStatusTransitionMessage(this.workflow.savedStatus, this.workflow.pendingStatus, context);
    },
    isBlocked() {
      return !!this.transitionMessage && this.transitionMessage.type === 'error';
    },
    validateButtonParams() {
      return {text: 'Valider', disabled: this.isBlocked };
    },
    businessSelectValues() {
      return possibleBusinessStatusSelect(this.business);
    },
    businessReadOnly() {
      return isBusinessReadOnly(this.business);
    },
    creditNoteSelectValues() {
      return this.creditNote ? possibleCreditNoteStatusSelect(this.creditNote) : [];
    },
  },
  watch: {
    business: {
      immediate: true,
      handler(business) {
        this.selfDepositStatus = undefined;
        // Le contrat a été rechargé : les encaissements chargés ne sont plus à jour
        this.collections = [];
        this.collectionsLoadedFor = undefined;
        if (business) this.selfInputs.value = business.status;
      },
    },
    businessSelectValues: {
      immediate: true,
      handler(values) {
        this.selfInputs.selectValues = values;
      },
    },
    businessReadOnly: {
      immediate: true,
      handler(value) {
        this.selfInputs.readOnly = value;
      },
    },
    isPaymentTarget: {immediate: true, handler: 'loadCollections'},
    creditNoteSelectValues: {immediate: true, handler: 'syncCreditNoteInputs'},
    creditNoteInputs: {immediate: true, handler: 'syncCreditNoteInputs'},
  },
  methods: {
    syncCreditNoteInputs() {
      if (this.selfCreditNoteInputs) this.selfCreditNoteInputs.status.selectValues = this.creditNoteSelectValues;
    },
    async loadCollections() {
      if (!this.isPaymentTarget || !this.business || !this.business.id || this.collectionsLoadedFor === this.business.id) return;
      const resp = await CollectionController.getAllByBusiness(this.business.id, '');
      this.collections = (resp && !resp.errors) ? resp : [];
      this.collectionsLoadedFor = this.business.id;
    },
    selectStep(step) {
      if (this.isReadOnly || !step.isSelectable) return;
      if (this.isDeposit) {
        // Cliquer sur le statut réel annule la sélection
        this.selfDepositStatus = step.value === getDepositStatus(this.business) ? undefined : step.value;
      } else if (!this.isCreditNote) {
        this.selfInputs.value = step.value;
      } else if (this.selfCreditNoteInputs) {
        this.selfCreditNoteInputs.status.value = step.value;
      }
    },
    openSignModal() {
      if (this.signCallback) this.signCallback();
    },
    validateStatus() {
      if (!this.hasPendingChange || this.validateButtonParams.disabled) return;

      // Passage forcé à payé / remboursé : confirmation dans une modale (avec proposition d'encaissement associé)
      if (this.isPaymentTarget) {
        const pendingStep = this.workflow.steps.find(step => step.value === this.workflow.pendingStatus);
        this.$refs.forceStatusModal.openModal({
          documentLabel: this.isCreditNote ? "cet avoir" : (this.isDeposit ? "cet acompte" : "cette facture"),
          statusLabel: pendingStep ? pendingStep.label : this.workflow.pendingStatus,
          proposal: this.collectionProposal
        });
        return;
      }

      this.applyStatus();
    },
    // collectionOptions : {addCollection, date} si le passage forcé est confirmé depuis la modale
    async applyStatus(collectionOptions) {
      const proposal = (collectionOptions && collectionOptions.addCollection) ? this.collectionProposal : undefined;
      let res;

      if (this.isDeposit) {
        res = await BusinessController.patchDepositStatus(this.business.id, {status: this.workflow.pendingStatus});
      } else if (this.isCreditNote) {
        res = await BusinessController.patchCreditNoteStatus(this.creditNote.number, {status: this.workflow.pendingStatus})
      } else {
        const body = {status: this.workflow.pendingStatus};
        if (proposal) body.paymentDate = collectionOptions.date;
        res = await BusinessController.patchStatus(this.business.id, body);
      }

      if (!res.errors) {
        if (proposal) {
          const collectionRes = await CollectionController.post({
            amount: proposal.amount,
            paymentType: PAYMENT_TYPE.TRANSFER.value,
            paymentPurpose: proposal.paymentPurpose,
            creditNoteNumber: proposal.creditNoteNumber,
            date: collectionOptions.date,
            businessId: this.business.id
          });
          if (collectionRes.errors) {
            (new NotificationService).addNotification("danger", "Statut modifié, mais l'encaissement associé n'a pas pu être ajouté");
          }
        }
        (new NotificationService).addNotification("success", "Statut modifié");
        if (this.callback) this.callback();
      }
    },
  },
};
</script>

<style lang="stylus" scoped>
.status-pipeline-track
  display flex
  flex-direction column
  position relative

.status-pipeline-unmanaged
  padding 10px 12px
  border 1px dashed #9aa1ad
  border-radius 6px
  background-color #eceef1
  font-size 12px
  color #4a5160

.status-pipeline-validation
  display flex
  flex-direction column
  gap 8px
  margin-top 12px

.status-transition-message
  display flex
  gap 6px
  margin 0
  padding 8px 10px
  border-radius 6px
  font-size 12px
  line-height 1.4

  &.is-warning
    background-color #fff6e5
    color #6e4600

  &.is-error
    background-color #fbeeee
    color #8f2a2a

  // Information neutre : ce qui peut se passer sans action de l'utilisateur
  &.is-info
    border 1px solid #c9d5ec
    background-color #e9eef8
    color #2a3f6b

    .status-transition-icon
      color #3a5a8f

.status-transition-icon
  flex 0 0 auto
</style>